import PropTypes from "prop-types";
import React from "react";
import { useDispatch, useSelector as useReduxSelector } from "react-redux";
import LoadingSpinner from "web/react/components/loading-spinner/loading-spinner";
import Button from "web/react/components/button/button";
import { hideSeoContent } from "web/redux/ducks/feed-seo-content";
import { ReduxStoreState } from "web/redux/store";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import userProfiler from "web/script/modules/userprofiler";
import { numberFormat } from "web/script/modules/formats";
import browser from "web/script/utils/browser";
import { RawBranchLink } from "web/script/utils/branch-io";
import { useOverlayWatcher } from "web/react/hooks/use-overlay-watcher/use-overlay-watcher";
import history from "web/script/utils/history";
import SendToAppModal from "web/react/components/send-to-app-modal/send-to-app-modal";
import classnames from "classnames";
import globals from "web/script/modules/globals";
import { setLoading } from "web/redux/ducks/feed-show-more";
import styles from "./feeds-show-more.css";

interface FeedShowMoreProps {
    initialVisibleProductCount: number;
    initialTotalProductCount: number;
    initialNextButtonUrl: string;
    initialCurrentPage: number;
    initialRemainingProductCount: number;
    buttonText: string;
}

const TITLE_1 = "title_1";
const TITLE_2 = "title_2";
const TITLE_3 = "title_3";
const TITLE_4 = "title_4";
const APP_MODAL_NAME = "send-to-app-on-feed-show-more-modal";

function feedShowMoreState(state: ReduxStoreState): any {
    return state.feedShowMoreReducer;
}

function ProgressBar({ currentNumber, maximumNumber }): JSX.Element {
    const progress = currentNumber / maximumNumber;
    return (
        <div className={styles.progressBar} role="presentation">
            <span
                className={styles.progressBarInner}
                style={{
                    width: progress * 100 + "%",
                }}
            />
        </div>
    );
}

export function FeedsShowMore({
    initialCurrentPage,
    initialVisibleProductCount,
    initialTotalProductCount,
    initialNextButtonUrl,
    initialRemainingProductCount,
    buttonText,
}: FeedShowMoreProps): JSX.Element | null {
    const feedState = useReduxSelector(feedShowMoreState);
    const dispatch = useDispatch();
    let {
        currentPage,
        visibleProductCount,
        totalProductCount,
        showMoreNextUrl,
        isLoading,
        remainingProducts,
    } = feedState;
    const isPaidChannel = analytics.isPaidChannel();
    const blockPaidChannel = !!environment.getFeature("mg_paid_show_more");
    const blockOtherChannels = !!environment.getFeature("mg_others_block_show_more");

    let shouldBlock = false;
    if (browser.ios && !userProfiler.isLoggedIn()) {
        shouldBlock = isPaidChannel ? blockPaidChannel : blockOtherChannels;
    }

    const blockModalIsOpen = !!useOverlayWatcher(APP_MODAL_NAME);

    // Initial values for server-side rendering
    if (currentPage === null) {
        currentPage = initialCurrentPage;
        visibleProductCount = initialVisibleProductCount;
        totalProductCount = initialTotalProductCount;
        showMoreNextUrl = initialNextButtonUrl;
        remainingProducts = initialRemainingProductCount;
    }

    const titleVariant = environment.getFeature("mg_block_show_more_title");
    let blockingTitle = "";
    if (titleVariant == TITLE_1) {
        // "Get personalized results from your favorite brands"
        blockingTitle = gettext("feed.show_more.app_overlay_block.title_1");
    } else if (titleVariant == TITLE_2) {
        // "Keep track of your favorite items all in one place"
        blockingTitle = gettext("feed.show_more.app_overlay_block.title_2");
    } else if (titleVariant == TITLE_3) {
        // "Shop the world’s biggest and best selection of fashion"
        blockingTitle = gettext("feed.show_more.app_overlay_block.title_3");
    } else if (titleVariant == TITLE_4) {
        // "The hottest and newest products from over 12,000 brands"
        blockingTitle = gettext("feed.show_more.app_overlay_block.title_4");
    }

    let messageText = "";
    if (totalProductCount > 0 || visibleProductCount == 0) {
        // "Showing {current_product_count} of {total_product_count}"
        messageText = gettext("feed_pagination.summary_showing_products", {
            current_product_count: numberFormat(visibleProductCount),
            total_product_count: numberFormat(remainingProducts),
        });
    }

    function showMore(): void {
        const pageViewId = history.getCurrentState().pageViewId;
        history.pushState(new URL(showMoreNextUrl, globals.window.location.href), {
            saveScrollPosition: false,
            data: { pageViewId },
        });
        dispatch(hideSeoContent());
        dispatch(setLoading(true));
    }

    function onBlockModalClose(): void {
        history.popOverlay(APP_MODAL_NAME);
    }

    function onBlockModalOpen(): void {
        // I check the current overlay state instead of modalIsOpen
        // to get around the delay introduced by hooks
        if (history.getCurrentState().overlay != APP_MODAL_NAME) {
            history.pushOverlay(APP_MODAL_NAME);
        }
    }

    function onClickShowMore(event: React.MouseEvent<HTMLAnchorElement>): void {
        event.preventDefault();
        analytics.event("feeds_show_more", "click_show_more_button", "click");
        if (shouldBlock) {
            onBlockModalOpen();
            return;
        }
        showMore();
    }

    let loadingHtml: JSX.Element | null = null;
    if (isLoading) {
        loadingHtml = (
            <div className={styles.loadingWrapper}>
                <LoadingSpinner />
            </div>
        );
    }

    const paywalledClassName = "paywalled-content";

    return (
        <div className={classnames(styles.wrapper, { [paywalledClassName]: shouldBlock })}>
            <div className={styles.loadingWrapper}>{loadingHtml}</div>

            <div className={styles.progressMessage}>
                <p>{messageText}</p>
            </div>

            <ProgressBar currentNumber={visibleProductCount} maximumNumber={remainingProducts} />

            {showMoreNextUrl && (
                <Button
                    className={styles.showMoreButton}
                    href={showMoreNextUrl}
                    fullHeight={false}
                    fullWidth={false}
                    openInNewTab={false}
                    disabled={false}
                    styleType={"tertiary"}
                    onClick={onClickShowMore}
                >
                    {buttonText}
                </Button>
            )}

            <SendToAppModal
                title={blockingTitle}
                url={RawBranchLink.ShowMore}
                isOpen={blockModalIsOpen}
                onClose={onBlockModalClose}
                analyticsEventLabel={"feeds_show_more"}
                paywalledClassName={shouldBlock ? paywalledClassName : null}
            />
        </div>
    );
}

ProgressBar.propTypes = {
    currentNumber: PropTypes.number,
    maximumNumber: PropTypes.number,
};

ProgressBar.defaultProps = {
    currentNumber: 0,
    maximumNumber: 1,
};

export default FeedsShowMore;
